// client/src/pages/RegisterPage.jsx - Page d'inscription
import React, { useState, useContext, useEffect} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import { PersonCounter, PreferenceCategory, IngredientsSelector } from '../components/utils/PersonalizedForm';
import allergiesList from "../data/allergiesList"; 
import dislikedIngredientsList from "../data/dislikedIngredientsList"; 
import Seo from '../components/Seo';

const RegisterPage = () => {
    const [formData, setFormData] = useState({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
    });
    const [error, setError] = useState(null);
    const [currentStep, setCurrentStep] = useState(1);
    const [preferences, setPreferences] = useState({
        dietaryRestrictions: [],
        dietaryRestrictionsComment:"",
        goals: [],
        goalsComment:"",
        allergies: [],
        dislikedIngredients: [],
        cuisinePreferences: [],
        childrenCount: 0,
        adultCount: 2,
      });

    const { register, user } = useContext(AuthContext);
    const navigate = useNavigate();
    const { name, email, password, confirmPassword } = formData;

    const seo = (
      <Seo
        title="Inscription"
        description="Créez votre compte YAQAM pour planifier vos repas et générer des recettes personnalisées."
        breadcrumb
        noindex
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "YAQAM",
            "url": (typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : 'https://www.yaqam.com',
            "logo": "https://www.yaqam.com/img/logo_color-min.png",
            "contactPoint": [{
              "@type": "ContactPoint",
              "contactType": "customer support",
              "email": "info@j7lab.com"
            }]
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Accueil",
                "item": (typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : 'https://www.yaqam.com'
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Inscription",
                "item": ((typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : 'https://www.yaqam.com') + (typeof window !== 'undefined' ? window.location.pathname : '/register')
              }
            ]
          }
        ]}
      />
    );
    
    // Rediriger si déjà connecté
    useEffect(() => {
      if (user) {
        navigate('/dashboard');
      }
    }, [user, navigate]);

    //Gestion des données utilisateur du formulaire (étape 1):user password...
    const handleChange = (e) => {
      setFormData({
        ...formData,
        [e.target.name]: e.target.value,
      });
    };
    
    //Gestion universelle des préf
    const handlePreferencesChange = (ingredients, type) => {
        setPreferences(prev => ({
          ...prev,
          [type]: ingredients
        }));
      };
    
    const handleNextStep = (e) => {
      e.preventDefault();
      
      // Validation du premier formulaire
      if (currentStep === 1) {
        if (password !== confirmPassword) {
          return setError('Les mots de passe ne correspondent pas');
        }
        
        if (password.length < 6) {
          return setError('Le mot de passe doit contenir au moins 6 caractères');
        }
      }
      
      // Validation étape 2 (nombre de personnes)
      if (currentStep === 2 && (preferences.adultCount + preferences.childrenCount) === 0) {
        return setError('Veuillez sélectionner au moins une personne');
      }
      
      setError(null);
      setCurrentStep(currentStep + 1);
    };
    
    const handlePrevStep = () => {
      setCurrentStep(currentStep - 1);
      setError(null);
    };
    
    const handleSubmit = async (e) => {
      e.preventDefault();
      
      try {
        setError(null);
        await register(name, email, password, preferences);
        navigate('/dashboard');
      } catch (error) {
        setError(error.message);
      }
    };
    
    //Quand l'utilisateur clique sur une image
    // Fonction qui ajoute ou retire un item d'une catégorie de préférences (ex: 'allergies', 'favorites', etc.)
    const togglePreference = (category, item) => {
        // Mise à jour du state 'preferences' en fonction de sa valeur précédente
        setPreferences(prev => ({
        ...prev, // On garde toutes les autres catégories inchangées
    
        [category]: prev[category].includes(item) // Si l'item existe déjà dans la catégorie...
            ? prev[category].filter(i => i !== item) // ...on le retire (toggle OFF)
            : [...prev[category], item] // ...sinon on l'ajoute à la liste (toggle ON)
        }));
    };
    
    // Indicateur de progression
    const ProgressBar = ({ currentStep, number }) => (
        <div className="mb-6">
          <div className="flex justify-between mb-2">
            {Array.from({ length: number }, (_, i) => i + 1).map(step => (
              <div 
                key={step} 
                className={`w-8 h-8 rounded-full flex items-center justify-center 
                  ${currentStep >= step ? 'text-white' : 'text-gray-600'}`}
                style={currentStep >= step 
                  ? { backgroundColor: '#87A96B' } 
                  : { backgroundColor: '#e5e7eb' }
                }
              >
                {step}
              </div>
            ))}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="h-2 rounded-full" 
              style={{ 
                width: `${(currentStep / number) * 100}%`,
                backgroundColor: '#87A96B'
              }}
            ></div>
          </div>
        </div>
      );
  
    return (
      <>
        {seo}
        <div className="max-w-lg mx-auto px-4">
        <h1 className="text-3xl font-bold mb-4 text-center" style={{ color: '#2C3E50' }}>Inscription</h1>
        <ProgressBar currentStep={currentStep} number={5}/>
        
        {error && (
          <div className="p-4 rounded-lg mb-6" style={{ backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}>
            {error}
          </div>
        )}
        
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6">
            {/* Étape 1: Informations de base */}
            {currentStep === 1 && (
              <form onSubmit={handleNextStep}>
                <div className="mb-6">
                  <label htmlFor="name" className="block font-medium mb-2" style={{ color: '#2C3E50' }}>
                    Nom
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={name}
                    onChange={handleChange}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:border-transparent"
                    style={{ accentColor: '#87A96B' }}
                    required
                  />
                </div>
                
                <div className="mb-6">
                  <label htmlFor="email" className="block font-medium mb-2" style={{ color: '#2C3E50' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    onChange={handleChange}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:border-transparent"
                    style={{ accentColor: '#87A96B' }}
                    required
                  />
                </div>
                
                <div className="mb-6">
                  <label htmlFor="password" className="block font-medium mb-2" style={{ color: '#2C3E50' }}>
                    Mot de passe
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={password}
                    onChange={handleChange}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:border-transparent"
                    style={{ accentColor: '#87A96B' }}
                    required
                    minLength="6"
                  />
                </div>
                
                <div className="mb-6">
                  <label htmlFor="confirmPassword" className="block font-medium mb-2" style={{ color: '#2C3E50' }}>
                    Confirmer le mot de passe
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={handleChange}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:border-transparent"
                    style={{ accentColor: '#87A96B' }}
                    required
                  />
                </div>
                
                <div className="mb-6">
                  <button
                    type="submit"
                    className="w-full py-3 text-white rounded-full font-medium hover:opacity-90 transition-all"
                    style={{ backgroundColor: '#87A96B' }}
                  >
                    Suivant
                  </button>
                </div>
                
                <div className="text-center" style={{ color: '#6c757d' }}>
                  <p>
                    Déjà un compte?{' '}
                    <Link to="/login" className="hover:opacity-80 transition-all" style={{ color: '#2C3E50' }}>
                      Se connecter
                    </Link>
                  </p>
                </div>
              </form>
            )}
            
            {/* Étape 2: Composition du foyer */}
            {currentStep === 2 && (
              <form onSubmit={handleNextStep} className="px-2">
                <h2 className="text-xl font-semibold mb-6 text-center" style={{ color: '#2C3E50' }}>
                  Pour combien de personnes cuisinez-vous ?
                </h2>
                
                <div className="flex justify-center space-x-10 mb-2">
                  <PersonCounter 
                    label="Adultes"
                    count={preferences.adultCount}
                    onIncrement={() => setPreferences({...preferences, adultCount: preferences.adultCount + 1})}
                    onDecrement={() => setPreferences({...preferences, adultCount: Math.max(0, preferences.adultCount - 1)})}
                    imageUrl="./img/adult.png"
                  />
                  
                  <PersonCounter 
                    label="Enfants"
                    count={preferences.childrenCount}
                    onIncrement={() => setPreferences({...preferences, childrenCount: preferences.childrenCount + 1})}
                    onDecrement={() => setPreferences({...preferences, childrenCount: Math.max(0, preferences.childrenCount - 1)})}
                    imageUrl="./img/children.png"
                  />
                </div>
                
                <div className="text-center text-sm mt-3 mb-6" style={{ color: '#6c757d' }}>
                  {preferences.adultCount + preferences.childrenCount > 0 ? (
                    <p>Repas prévu pour {preferences.adultCount + preferences.childrenCount} {preferences.adultCount + preferences.childrenCount > 1 ? 'personnes' : 'personne'}</p>
                  ) : (
                    <p style={{ color: '#E67E22' }}>Veuillez sélectionner au moins une personne</p>
                  )}
                </div>
                
                <div className="border-b border-gray-200 w-full mb-6"></div>
                
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={handlePrevStep}
                    className="w-1/3 py-3 bg-gray-200 rounded-full font-medium hover:bg-gray-300 transition-all"
                    style={{ color: '#2C3E50' }}
                  >
                    Précédent
                  </button>
                  
                  <button
                    type="submit"
                    disabled={preferences.adultCount + preferences.childrenCount === 0}
                    className={`w-2/3 py-3 text-white rounded-full font-medium transition-all ${
                      preferences.adultCount + preferences.childrenCount === 0 
                        ? "opacity-50 cursor-not-allowed" 
                        : "hover:opacity-90"
                    }`}
                    style={{ backgroundColor: '#87A96B' }}
                  >
                    Suivant
                  </button>
                </div>
              </form>
            )}
            
            {/* Étape 3: Objectifs */}
            {currentStep === 3 && (
              <form onSubmit={handleNextStep} className="px-2">
                <h2 className="text-xl font-semibold mb-6 text-center" style={{ color: '#2C3E50' }}>
                Quels sont vos objectifs ?
                </h2>
                
                <div className="mb-8">
                  <div className="flex flex-wrap gap-2 mb-6">
                  <PreferenceCategory
                        title="L'application doit m'aider à..."
                        color="green"
                        items={[
                        { name: "Perte de poids", img: "./img/lose-weight.png", icon: "" },
                        { name: "Booster ses performances", img: "./img/performance.png", icon: "" },
                        { name: "Diminuer le stress pour élaborer les repas", img: "./img/zen.png", icon: "" },
                        { name: "Mieux planifier ma semaine", img: "./img/planify.png", icon: "" },
                        { name: "Faire des économies", img: "./img/economy.png", icon: "" },
                        { name: "Découvrir des plats adaptés à mes goûts", img: "./img/taste.png", icon: "" },
                        { name: "Privilégier une alimentation saine", img: "./img/healthy.png", icon: "" },
                        { name: "Adapter mes repas à un régime alimentaire/carences", img: "./img/regime.png", icon: "" },
                        ]}
                        selectedItems={preferences.goals}
                        onToggle={togglePreference}
                        inputName="goals"
                        placeholder="Préciser ou ajouter des régimes alimentaires..."
                        cols={2}
                    />
                  </div>
                </div>
                
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={handlePrevStep}
                    className="w-1/3 py-3 bg-gray-200 rounded-full font-medium hover:bg-gray-300 transition-all"
                    style={{ color: '#2C3E50' }}
                  >
                    Précédent
                  </button>
                  
                  <button
                    type="submit"
                    className="w-2/3 py-3 text-white rounded-full font-medium hover:opacity-90 transition-all"
                    style={{ backgroundColor: '#87A96B' }}
                  >
                    Suivant
                  </button>
                </div>
              </form>
            )}
            
            {/* Étape 4: Préférences culinaires */}
            {currentStep === 4 && (
              <form onSubmit={handleNextStep} className="px-2">
                <h2 className="text-xl font-semibold mb-6 text-center" style={{ color: '#2C3E50' }}>
                Suivez-vous un régime alimentaire ?
                </h2>
                
                <div className="mb-8">
                    <div className="flex flex-wrap gap-2 mb-6">
                    <PreferenceCategory
                        title="Je suis le/les régimes suivants..."
                        color="green"
                        items={[
                        { name: "Végétarien", img: "./img/vegetarian.png", icon: "" },
                        { name: "Végan", img: "./img/vegan.png", icon: "" },
                        { name: "Sans gluten", img: "./img/gluten-free.png", icon: "" },
                        { name: "Sans lactose", img: "./img/lactose-free.png", icon: "" },
                        { name: "Cétogène", img: "./img/cetogene.png", icon: "" },
                        { name: "Halal", img: "./img/halal.png", icon: "" }
                        ]}
                        selectedItems={preferences.dietaryRestrictions}
                        onToggle={togglePreference}
                        inputName="dietaryRestrictions"
                        placeholder="Ajouter ou préciser les restrictions..."
                        cols={2}
                    />
                    </div>
                </div>
                
                <div className="flex space-x-3">
                    <button
                    type="button"
                    onClick={handlePrevStep}
                    className="w-1/3 py-3 bg-gray-200 rounded-full font-medium hover:bg-gray-300 transition-all"
                    style={{ color: '#2C3E50' }}
                    >
                    Précédent
                    </button>
                    
                    <button
                    type="submit"
                    className="w-2/3 py-3 text-white rounded-full font-medium hover:opacity-90 transition-all"
                    style={{ backgroundColor: '#87A96B' }}
                    >
                    Suivant
                    </button>
                </div>
              </form>
            )}
            
            {/* Étape 5: Allergies et aliments à éviter */}
            {currentStep === 5 && (
              <form onSubmit={handleNextStep} className="px-2">
                <h2 className="text-xl font-semibold mb-6 text-center" style={{ color: '#2C3E50' }}>
                    Quelles sont les allergies et aliments je n'aime pas ?
                </h2>
                
                <div className="mb-8">
                    <h3 className="text-lg font-medium mb-3" style={{ color: '#2C3E50' }}>Allergies</h3>
                    <div className="mb-6">
                        <IngredientsSelector onChange={(e)=>handlePreferencesChange(e, 'allergies')} ingredientsList={allergiesList} initialSelected={preferences.allergies}/>
                    </div>
                    <h3 className="text-lg font-medium mb-3" style={{ color: '#2C3E50' }}>Ingrédients à éviter</h3>
                    {/* Composant de sélection d'ingrédients */}
                    <div className="mb-6">
                    <IngredientsSelector onChange={(e)=>handlePreferencesChange(e, 'dislikedIngredients')} ingredientsList={dislikedIngredientsList} initialSelected={preferences.dislikedIngredients}/>
                    </div>
                </div>
                
                <div className="flex space-x-3">
                    <button
                    type="button"
                    onClick={handlePrevStep}
                    className="w-1/3 py-3 bg-gray-200 rounded-full font-medium hover:bg-gray-300 transition-all"
                    style={{ color: '#2C3E50' }}
                    >
                    Précédent
                    </button>
                    
                    <button
                    type="submit"
                    onClick={handleSubmit}
                    className="w-2/3 py-3 text-white rounded-full font-medium hover:opacity-90 transition-all"
                    style={{ backgroundColor: '#87A96B' }}
                    >
                    Terminer
                    </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
      </>
    );
  };

export default RegisterPage;
